import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { fetchVIX } from "@/lib/api";
import { tooltipStyle, tooltipLabelStyle } from "@/lib/chartTheme";

const RANGES = [
  { label: "1M", days: 30 },
  { label: "3M", days: 90 },
  { label: "6M", days: 180 },
  { label: "1Y", days: 365 },
];

// VIX regime thresholds (CBOE convention)
const REGIMES = [
  { max: 12, label: "COMPLACENT", color: "text-zinc-400" },
  { max: 20, label: "CALM", color: "text-pos" },
  { max: 30, label: "ELEVATED", color: "text-amber-term" },
  { max: 1000, label: "STRESS", color: "text-neg" },
];

function regimeFor(v) {
  return REGIMES.find((r) => v <= r.max) || REGIMES[REGIMES.length - 1];
}

export default function VIXPanel() {
  const [days, setDays] = useState(180);
  const q = useQuery({
    queryKey: ["vix", days],
    queryFn: () => fetchVIX(days),
    staleTime: 1000 * 60 * 15,
  });

  const latest = q.data?.latest;
  const points = q.data?.points || [];
  const chg1d = q.data?.change_1d;
  const chg1w = q.data?.change_1w;
  const regime = latest ? regimeFor(latest.vix) : null;

  const sign = (v) =>
    v == null ? "text-zinc-500" : v > 0 ? "text-neg" : v < 0 ? "text-pos" : "text-zinc-500";
  const fmtPts = (v) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)} pts`;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="vix-panel">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400">
            ▍ VIX · CBOE VOLATILITY INDEX
          </span>
          {regime && (
            <span
              className={`text-[11px] uppercase tracking-widest border border-zinc-800 px-1.5 py-0.5 ${regime.color}`}
              data-testid="vix-regime"
            >
              {regime.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1" data-testid="vix-range-tabs">
          {RANGES.map((r) => {
            const active = r.days === days;
            return (
              <button
                key={r.days}
                data-testid={`vix-range-${r.label}`}
                onClick={() => setDays(r.days)}
                className={`px-2 py-1 text-[11px] uppercase tracking-widest border ${
                  active
                    ? "border-amber-500 bg-amber-500/10 text-amber-term"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4">
        <div className="md:col-span-1 border-r border-zinc-800 p-3 flex flex-col justify-center gap-1.5">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">SPOT</div>
          <div className="text-3xl font-semibold tabular-nums text-amber-term leading-none">
            {latest ? latest.vix.toFixed(2) : q.isLoading ? "…" : "—"}
          </div>
          <div className={`text-xs tabular-nums ${sign(chg1d)}`}>{fmtPts(chg1d)} 1D</div>
          <div className={`text-xs tabular-nums ${sign(chg1w)}`}>{fmtPts(chg1w)} 1W</div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-600 mt-1">
            {latest?.date} · {points.length} obs
          </div>
        </div>

        <div className="md:col-span-3 p-2 h-[200px]">
          {q.isLoading ? (
            <div className="h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
              Loading VIX…
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="vixGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  stroke="#27272A"
                  tickFormatter={(d) => (d ? d.slice(5) : "")}
                  minTickGap={28}
                />
                <YAxis
                  tick={{ fill: "#71717A", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  stroke="#27272A"
                  tickFormatter={(v) => v.toFixed(0)}
                  width={36}
                  domain={["auto", "auto"]}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={tooltipLabelStyle}
                  formatter={(v) => [`${v?.toFixed(2)}`, "VIX"]}
                />
                <ReferenceLine y={20} stroke="#3F3F46" strokeDasharray="2 2" label={{ value: "20", fill: "#52525B", fontSize: 10, position: "insideTopRight" }} />
                <ReferenceLine y={30} stroke="#3F3F46" strokeDasharray="2 2" label={{ value: "30", fill: "#52525B", fontSize: 10, position: "insideTopRight" }} />
                <Area
                  type="linear"
                  dataKey="vix"
                  stroke="#EF4444"
                  strokeWidth={1.75}
                  fill="url(#vixGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
